// polygon-tool.ts
import * as Cesium from 'cesium';

// STUBS: Replace with your real implementations or imports!
class PolygonPrimitive {
  positions: any;
  asynchronous: boolean = false;
  _createPrimitive: boolean = false;
  constructor(options: any) {}
}
class BillboardGroup {
  constructor(tool: any, defaultBillboard: any, scene: any) {}
  addBillboard(cartesian: any) {}
  remove() {}
}
const defaultBillboard = {};
const ellipsoid = Cesium.Ellipsoid.WGS84;

export class PolygonTool {
  private _tooltip: any;
  private positions: any[] = [];
  private editCleanUp: (() => void) | null = null;
  private freeze: (() => void) | null = null;
  private mouseHandler: Cesium.ScreenSpaceEventHandler | null = null;

  constructor(private sceneToUse: any, private containerToUse: any) {
    if (!containerToUse) {
      throw new Error('PolygonTool: containerToUse is undefined. You must pass a valid DOM element.');
    }
    this._tooltip = this.createTooltip(this.containerToUse);
  }

  private createTooltip(frameDiv: any) {
    class Tooltip {
      _div: HTMLDivElement;
      _title: HTMLDivElement;
      constructor(frameDiv: any) {
        const div = document.createElement('DIV') as HTMLDivElement;
        div.className = 'twipsy right';

        const arrow = document.createElement('DIV');
        arrow.className = 'twipsy-arrow';
        div.appendChild(arrow);

        const title = document.createElement('DIV') as HTMLDivElement;
        title.className = 'twipsy-inner';
        div.appendChild(title);

        this._div = div;
        this._title = title;

        frameDiv.appendChild(div);
      }

      setVisible(visible: boolean) {
        this._div.style.display = visible ? 'block' : 'none';
      }

      showAt(position: any, message: string) {
        if (position && message) {
          this.setVisible(true);
          this._title.innerHTML = message;
          this._div.style.left = position.x + 10 + 'px';
          this._div.style.top = (position.y - this._div.clientHeight / 2) + 'px';
        }
      }
    }
    return new Tooltip(frameDiv);
  }

  startDrawing(cleanUp: () => void) {
    if (this.editCleanUp) {
      this.editCleanUp();
    }
    this.editCleanUp = cleanUp;
  }

  stopDrawing() {
    if (this.editCleanUp) {
      this.editCleanUp();
      this.editCleanUp = null;
    }
  }

  stopDrawingShape() {
    this.stopDrawing();
  }

  setFreezeDrawing(freezeDrawFunc: () => void) {
    this.freeze = freezeDrawFunc;
  }

  freezeDrawing() {
    if (this.freeze) {
      this.freeze();
    }
  }

  freezeDrawingShape() {
    this.freezeDrawing();
  }

  startDrawingShape(isPolygon: boolean, options: any) {
    const scene = this.sceneToUse;
    const primitives = scene.primitives;
    const tooltip = this._tooltip;

    const minPoints = isPolygon ? 2 : 2;
    const polygonGeometry = new Cesium.PolygonGeometry({
      polygonHierarchy: new Cesium.PolygonHierarchy(this.positions),
      vertexFormat: Cesium.PerInstanceColorAppearance.VERTEX_FORMAT,
    });
    const geometryInstance = new Cesium.GeometryInstance({
    geometry: polygonGeometry,
    attributes: {
      color: Cesium.ColorGeometryInstanceAttribute.fromColor(
        Cesium.Color.YELLOW.withAlpha(0.6)
      )
    }
  });
    const primitive = new Cesium.Primitive({
      geometryInstances: geometryInstance,
      appearance: new Cesium.PerInstanceColorAppearance({
        closed: true
      })
    });
    scene.primitives.add(primitive);

    this.positions = [];
    const markers = new BillboardGroup(this, defaultBillboard, scene);

    this.mouseHandler = new Cesium.ScreenSpaceEventHandler(scene.canvas);

    this.startDrawing(() => {
      scene.primitives.remove(primitive);
      markers.remove();
      if (this.mouseHandler) {
        this.mouseHandler.destroy();
        this.mouseHandler = null;
      }
      tooltip.setVisible(false);
      this.positions = [];
    });

    this.setFreezeDrawing(() => {
      markers.remove();
      if (this.mouseHandler) {
        this.mouseHandler.destroy();
        this.mouseHandler = null;
      }
      tooltip.setVisible(false);
    });

    // LEFT_CLICK
    this.mouseHandler?.setInputAction((movement: any) => {
      if (movement.position !== null) {
        const cartesian = scene.camera.pickEllipsoid(movement.position, ellipsoid);
        if (cartesian) {
          if (this.positions.length === 0) {
            // first click
          }
          // if (this.positions.length >= minPoints) {
            // This part of the logic needs to be updated to use the new primitive
            // For now, we'll just add the point to the positions array
            // this.positions.push(cartesian);
            // markers.addBillboard(cartesian);
            this.positions.push(cartesian);
markers.addBillboard(cartesian);
          // }
        }
      }
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

    // MOUSE_MOVE
    this.mouseHandler?.setInputAction((movement: any) => {
      const position = movement.endPosition;
      if (position !== null) {
        if (this.positions.length === 0) {
          tooltip.showAt(position, '<p>Click to add first point</p>');
        } else {
          const cartesian = scene.camera.pickEllipsoid(position, ellipsoid);
          if (cartesian) {
            tooltip.showAt(
              position,
              `<p>Click to add new point (${this.positions.length})</p>` +
                (this.positions.length > minPoints
                  ? '<p>Double click to finish drawing</p>'
                  : '')
            );
          }
        }
      }
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

    // LEFT_DOUBLE_CLICK
    this.mouseHandler?.setInputAction((movement: any) => {
      const position = movement.position;
      if (position !== null) {
        if (this.positions.length < minPoints + 2) {
          return;
        }
        const cartesian = scene.camera.pickEllipsoid(position, ellipsoid);
        if (cartesian) {
          this.stopDrawing();
          if (typeof options.callback === 'function') {
            options.callback(this.generatePositions(true));
          }
        }
      }
    }, Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);
  }

  private generatePositions(fromDoubleClick: boolean) {
    if (this.positions.length === 0) {
      return [];
    }
    let newPositions;
    if (fromDoubleClick) {
      newPositions = this.positions.slice(0, this.positions.length - 1);
    } else {
      newPositions = this.positions.slice(0, this.positions.length);
    }
    newPositions.push(this.positions[0]);
    return newPositions;
  }

  getCurrentShape() {
    return this.generatePositions(false);
  }
}